import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "./Icon";
import { visibleNav, type NavItem } from "../lib/nav";
import type { TranslationKey } from "../lib/i18n";
import { useConfig } from "../state/config-context";
import { useSession } from "../state/session-context";

function LangToggle(): ReactNode {
  const { lang, setLang, t } = useConfig();
  return (
    <div className="lang-toggle" role="group" aria-label={t("common.language")}>
      <button
        type="button"
        className={`lang-toggle__btn${lang === "es" ? " is-active" : ""}`}
        aria-pressed={lang === "es"}
        onClick={() => setLang("es")}
      >
        ES
      </button>
      <button
        type="button"
        className={`lang-toggle__btn${lang === "en" ? " is-active" : ""}`}
        aria-pressed={lang === "en"}
        onClick={() => setLang("en")}
      >
        EN
      </button>
    </div>
  );
}

function NavSection({
  titleKey,
  items,
  onNavigate,
  id,
}: {
  titleKey: TranslationKey;
  items: NavItem[];
  onNavigate: () => void;
  id: string;
}): ReactNode {
  const { t } = useConfig();
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="nav-section">
      <p className="nav-section__title" id={`${id}-title`}>
        {t(titleKey)}
      </p>
      <ul className="nav-list" aria-labelledby={`${id}-title`}>
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-link${isActive ? " nav-link--active" : ""}`}
              onClick={onNavigate}
            >
              <Icon name={item.icon} />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AppShell(): ReactNode {
  const { bootstrap, t } = useConfig();
  const { me, permissions, signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const items = visibleNav(permissions);
  const mainItems = items.filter((item) => item.section === "main");
  const crmItems = items.filter((item) => item.section === "crm");
  const supportItems = items.filter((item) => item.section === "support");
  const billingItems = items.filter((item) => item.section === "billing");
  const moduleItems = items.filter((item) => item.section === "modules");

  const onLogout = async (): Promise<void> => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const appName = bootstrap?.app.name ?? "";
  const logoUrl = bootstrap?.brand.logo_url ?? "";
  const displayName = me?.user.display_name ?? me?.user.email ?? "";
  const orgName = me?.org.name ?? "";

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        {t("a11y.skipToContent")}
      </a>
      <header className="app-header">
        <button
          type="button"
          className="app-header__menu"
          aria-expanded={menuOpen}
          aria-controls="app-nav"
          onClick={() => setMenuOpen((value) => !value)}
        >
          <span className="sr-only">{t("nav.openMenu")}</span>
          <span aria-hidden="true" className="app-header__menu-icon" />
        </button>
        <div className="app-header__brand">
          {logoUrl !== "" ? (
            <img className="app-header__logo" src={logoUrl} alt="" />
          ) : null}
          <span className="app-header__name">{appName}</span>
        </div>
        <div className="app-header__user">
          <span className="app-header__identity">
            <span className="app-header__display">{displayName}</span>
            {orgName !== "" ? <span className="app-header__org">{orgName}</span> : null}
          </span>
          <LangToggle />
          <button type="button" className="btn btn--ghost" onClick={() => void onLogout()}>
            {t("common.logout")}
          </button>
        </div>
      </header>

      <div className="app-body">
        <nav
          id="app-nav"
          className={`app-nav${menuOpen ? " app-nav--open" : ""}`}
          aria-label={bootstrap?.app.name ?? "main"}
        >
          <NavSection
            id="nav-main"
            titleKey="nav.sectionMain"
            items={mainItems}
            onNavigate={() => setMenuOpen(false)}
          />
          <NavSection
            id="nav-crm"
            titleKey="nav.sectionCrm"
            items={crmItems}
            onNavigate={() => setMenuOpen(false)}
          />
          <NavSection
            id="nav-support"
            titleKey="nav.sectionSupport"
            items={supportItems}
            onNavigate={() => setMenuOpen(false)}
          />
          <NavSection
            id="nav-billing"
            titleKey="nav.sectionBilling"
            items={billingItems}
            onNavigate={() => setMenuOpen(false)}
          />
          <NavSection
            id="nav-modules"
            titleKey="nav.sectionModules"
            items={moduleItems}
            onNavigate={() => setMenuOpen(false)}
          />
        </nav>

        <main id="main-content" className="app-main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
